import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DynamicForm from "../../stories/Forms/DynamicForm";
import useApis from "../../services/useAPI";
import { timeAdministeredSchema } from "../../stories/Forms/validationSchema";
import { timeAdministeredFields } from "../../stories/Forms/fields";
import { ErrorDisplay } from "../Errors/ErrorDisplay";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { ArrowRightCircleFill, XSquare } from "react-bootstrap-icons";
import { as_title } from "../../utils";
import { accessError, networkError } from "../../utils/constants";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import {
  calculateProgressSteps,
  updateProgressAfterCompletion,
} from "../../utils/utils";
import { Modal, Button, Spinner } from "react-bootstrap";
import NavigateButtons from "../../stories/Buttons/NavigateButtons";
import WaringIcon from "../../assets/icons/warning-icon.svg";
import ModalWaringIcon from "../../assets/icons/modal-warning-icon.svg";

/**
 * TimeAdministered component
 *
 * This component is responsible for collecting and activating test timestamp information
 * for the test kits. It includes a form to submit the timestamp and displays warnings about
 * exposure to STIs. It handles API calls to update the test timestamp and activate the test.
 *
 * @returns {JSX.Element} The rendered component containing the form and exposure warning modal.
 */

export default function TimeAdministered() {
  const [exposureWarningOpen, setExposureWarningOpen] = useState(false);
  const [hasAcknowledgedWarning, setHasAcknowledgedWarning] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [isShType, setIsShType] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [payload, setPayload] = useState(null);
  const { callApi, resultPost, serverError, resultGet } = useApis();
  const { primaryBarcode } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();
  const formikRef = useRef(null);

  const typeUrl = `/api/dashboard/check-test/${primaryBarcode}/`;
  const postApiUrl = `/api/dashboard/test-kits/timestamp`;
  const postActivatingUrl = `/api/dashboard/activating/`;

  useEffect(() => {
    setIsLoading(true);
    callApi({}, typeUrl, "GET");
  }, [primaryBarcode]);

  useEffect(() => {
    if (resultGet?.type) {
      setIsShType(resultGet.type.includes("sex"));
    }
  }, [resultGet]);

  const toggleExposureWarning = () => {
    setExposureWarningOpen(false);
    setHasAcknowledgedWarning(true); // Acknowledge the modal
    setShowModal(true);
  };

  useEffect(() => {
    if (resultPost) {
      setShowSpinner(false);
      if (!serverError) {
        auth?.showToast(
          "Kit activation",
          "Test kit activated successfully!",
          "../assets/mhc.png",
          <ArrowRightCircleFill />,
        );

        const activatingPayload = {
          activation_stage: "test-taken",
          code: primaryBarcode,
        };
        callApi(activatingPayload, postActivatingUrl, "POST");
        const newProgressSteps = updateProgressAfterCompletion(
          resultGet,
          resultGet?.type,
          location.pathname,
        );
        auth?.setProgressSteps(newProgressSteps);
        const redirect =
          primaryBarcode?.startsWith("C") || primaryBarcode?.startsWith("BT")
            ? `/wellness-assessment/${resultPost?.test_kit_id}`
            : `/assessment/${resultPost?.test_kit_id}/1`;
        navigate(redirect);
      }
    }
  }, [resultPost]);

  useEffect(() => {
    if (serverError) {
      setShowSpinner(false);
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
    setError(serverError);
  }, [serverError]);

  useEffect(() => {
    if (resultGet) {
      // Check permission first
      const newProgressSteps = calculateProgressSteps(
        resultGet,
        resultGet?.type,
        location.pathname,
      );
      auth?.setProgressSteps(newProgressSteps);
      if (resultGet.activation_stage === null) {
        setError(accessError);
      } else if (resultGet.activation_stage) {
        let allowedStages = [
          "collect-sample",
          "personal-details",
          "sh-consent",
          "test-taken",

        ];
        if (allowedStages.includes(resultGet.activation_stage)) {
          setError("");
        } else {
          setError(accessError);
        }
      }

      // Set type if available
      if (resultGet.type) {
        setIsShType(resultGet.type.includes("sex"));
      }

      // Data loading complete
      setIsLoading(false);
    }
  }, [resultGet]);

  const handleSubmit = async (formData) => {
    setData(formData);

    const { sample_collection_date, sample_collection_time } = formData;

    const now = new Date();
    const selectedDateTime = new Date(
      `${sample_collection_date}T${sample_collection_time}`,
    );
    selectedDateTime.setSeconds(now.getSeconds());

    const year = selectedDateTime.getFullYear();
    const month = String(selectedDateTime.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDateTime.getDate()).padStart(2, "0");
    const hours = String(selectedDateTime.getHours()).padStart(2, "0");
    const minutes = String(selectedDateTime.getMinutes()).padStart(2, "0");
    const seconds = String(selectedDateTime.getSeconds()).padStart(2, "0");

    const timezoneOffset = new Date().getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
    const offsetMinutes = Math.abs(timezoneOffset) % 60;
    const offsetSign = timezoneOffset > 0 ? "-" : "+";
    const formattedOffset = `${offsetSign}${String(offsetHours).padStart(
      2,
      "0",
    )}:${String(offsetMinutes).padStart(2, "0")}`;

    const test_ts = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${formattedOffset}`;
    const payloadData = { test_ts };
    setPayload(payloadData);

    // --- Now check for SH warning ---
    if (isShType && !hasAcknowledgedWarning) {
      setExposureWarningOpen(true);
      return;
    }

    // Otherwise, show the confirmation modal directly
    setShowModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!payload) return;
    setShowSpinner(true);
    await callApi(payload, `${postApiUrl}/${primaryBarcode}/`, "POST");
    setShowModal(false);
  };
  const formatToUKDate = (dateString) => {
    if (!dateString) return ""; // prevents the error
    const date = new Date(dateString);

    // if invalid date (e.g., "invalid date" string)
    if (Number.isNaN(date)) return dateString;

      return date.toLocaleDateString("en-GB", {timeZone: "Europe/London", });
  };

  // Early return for loading state
  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border" className="my-custom-spinner" />
      </div>
    );
  }

  // Show the spinner during form submission too
  if (isLoading || showSpinner || resultPost?.test_kit_id) {
    return (
      <div className="text-center">
        <Spinner animation="border" className="my-custom-spinner" />
      </div>
    );
  }

  // Only after loading is complete and error check is done, handle errors
  if (error === networkError) {
    return <ErrorDisplay error={error} consent={false} />;
  }

  if (error) {
    return <ErrorDisplay error={error} consent={false} />;
  }

  return (
    <>
      <div className="row justify-content-center time-administered-page">
        <div className="col-md-12">
          <StepProgressBar
            steps={auth?.progressSteps?.totalSteps}
            currentStep={auth?.progressSteps?.currentStep || 2}
            completedSteps={auth?.progressSteps?.completedSteps || 1}
            isCompleted={auth?.progressSteps?.completedSteps || 1}
            is_assessment_completed={false}
          />
          <p className="text-muted test-type-title">
            Test :{" "}
            <span>
              {resultGet?.test_kit_type ? as_title(resultGet?.test_kit_type) : <Spinner />}
            </span>
          </p>
          <div className="card important-warning d-flex flex-row align-items-start">
            <img src={WaringIcon} className="me-2" alt="warning-icon"></img>
            <div>
              <h6>Important</h6>
              <p className="text-muted mb-2">
                Collect your sample(s) using the instructions provided in your
                test kit. Please do this carefully and follow all steps as
                closely as possible. If the instructions aren’t followed
                correctly, your sample may not be of sufficient
                quality. This could prevent us being able to analyse all of your
                results and you may only receive a partial report.
              </p>

              <p className="text-muted mb-0">
                When you’re finished, enter the date and time you collected your
                sample below.
              </p>
            </div>
          </div>

          <div className="pt-2 time-form-wrapper">
            <h4>Sample collection</h4>
            <p className="helptext">The date and time you took your sample.</p>
            <DynamicForm
              formikRef={formikRef}
              fields={timeAdministeredFields}
              onSubmit={handleSubmit}
              initialValues={{ ...data }}
              validationSchema={timeAdministeredSchema}
            />
          </div>
          <NavigateButtons isTimefield={true} formikRef={formikRef}/>
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            className="time-modal"
          >
            <Modal.Header closeButton className="p-0 mb-2">
              <img
                src={ModalWaringIcon}
                className="me-2"
                alt="modal-warning-icon"
              ></img>
              <Modal.Title className="ageo-extra-bold mt-1">Important</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0 ageo-medium">
              <p className="mb-1 ageo-semibold">Have you collected your sample(s) as per the instructions in your test kit? </p>
              <p className="ageo-semibold">
                Please confirm that the date and time entered below are when you
                collected your sample(s):
              </p>
              <div className="data-time-section">
                <div className="px-5 py-3 ms-4">
                  <strong className="me-4 ms-3">Date</strong>
                  {"   "}
                  <span>{formatToUKDate(data?.sample_collection_date)}</span>
                  <br />
                  <strong className="me-4 ms-3">Time</strong>{" "}
                  <span>{data?.sample_collection_time}</span>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="p-0 fs-18 ageo-semibold">
              <div className="row w-100 m-0">
                <Button
                  className="btn btn-green mb-3"
                  variant="green"
                  onClick={handleConfirmSubmit}
                >
                  Confirm & Continue
                </Button>
                <Button
                  className="btn btn-white"
                  variant="secondary"
                  onClick={() => setShowModal(false)}
                >
                  Edit Date / Time
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
          <span className="d-none">{serverError || null}</span>
        </div>
      </div>
      <Modal
        className="time-warning-modal"
        show={exposureWarningOpen}
        onHide={toggleExposureWarning}
      >
        <Modal.Header closeButton>
          <Modal.Title>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            When testing for sexually transmitted infections (STIs), some
            infections do not show up immediately after sex. The testing window,
            or the period of time after exposure you will get an accurate
            result, can vary by infection type. For the most accurate test
            results, we recommend that you follow the accurate testing windows
            listed below.
          </p>
          <p>
            <strong>Chlamydia</strong> - 2 weeks after exposure
          </p>
          <p>
            <strong>Gonorrhoea</strong> - 2 weeks after exposure
          </p>
          <p>
            <strong>HIV</strong> - 45 days after exposure
          </p>
          <p>
            <strong>Syphilis</strong> - 12 weeks after exposure
          </p>
          <p>
            If you've had high-risk exposure to either syphilis or HIV, we
            recommend repeating the test again in 12 weeks.
          </p>
          <p>
            If you are unsure about whether you are within a testing window, you
            should do a test now and another test at a later date.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={toggleExposureWarning}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
